import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Tooltip } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { I18nService } from '../../../core/services/i18n.service';
import { SpsPaymentStatusComponent } from '../../../custom/components/sps-payment-status/sps-payment-status.component';
import { ChildrenMenuToggler } from '../../directives/children-menu-toggler.directive';
import { SpsMenuItem } from '../../interfaces/sps-menu-item';
import { LayoutService } from '../../services/layout.service';
import { SidebarMenuService } from '../../services/sidebar-menu.service';

@Component({
  selector: 'gc-menu',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    NgClass,
    ChildrenMenuToggler,
    Tooltip,
    SpsPaymentStatusComponent
  ],
  templateUrl: './gc-menu.component.html'
})
export class GcMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() menuInitialized: EventEmitter<GcMenuComponent> = new EventEmitter<GcMenuComponent>();

  constructor(public i18n: I18nService,
              public layoutService: LayoutService,
              public sidebarMenuService: SidebarMenuService) {
  }

  /**
   * All Html-Elements of the menu-item link-tags <a>
   * for keyboard focus-control
   */
  private links: HTMLLinkElement[] = [];
  /**
   * Subscription on sidebar-menu-service if payment-menuitems changed
   * @private
   */
  private refreshSubscription: Subscription;

  /**
   *Refresh menu-container
   */
  refreshMenuContainer: boolean = false;

  ngOnInit() {
  }

  ngAfterViewInit(): void {
    this.links = Array.from(document.querySelectorAll('.sidebar-menu-link')) as HTMLLinkElement[];
    this.refreshSubscription = this.sidebarMenuService.menuItemRefreshedHandler.subscribe(refresh => {
      if (refresh) {
        this.refreshMenuContainer = true;
        setTimeout(() => {
          this.refreshMenuContainer = false;
        }, 10);
        /**
         * Payment-Menu-Items List-Changed = refresh the links for key-control
         */
        setTimeout(() => {
          this.links = Array.from(document.querySelectorAll('.sidebar-menu-link')) as HTMLLinkElement[];
        }, 500);
      }
    });
    this.menuInitialized.emit(this);
  }

  ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

  /**
   * callback function for activating a main menu item
   * @param menuItem
   */
  activateMenuItem(menuItem: SpsMenuItem) {
    /**
     * focus on clicked item for accessibility
     */
    this.links[menuItem.linkIndex].focus();

    switch (menuItem.type) {
      case 'M':
        if (this.sidebarMenuService.activeParentMenu) {
          this.sidebarMenuService.activeParentMenu.active = false;
          this.sidebarMenuService.activeParentMenu.selected = false;


        }
        if (!menuItem?.active) {
          if (this.sidebarMenuService.activeChildMenu) {
            this.sidebarMenuService.activeChildMenu.active = false;
            this.sidebarMenuService.activeChildMenu.selected = false;
          }
          menuItem.active = true;
          menuItem.selected = true;
          this.sidebarMenuService.activeChildMenu = menuItem;
        } else {
          menuItem.selected = true;
        }
        break;
      case 'P':
        if (this.sidebarMenuService.activeChildMenu) {
          this.sidebarMenuService.activeChildMenu.selected = false;
        }

        /**
         * AutoExpand
         */
        let toggle = true;
        if (!this.layoutService.menuExpanded()) {
          this.layoutService.menuExpanded.set(true);
          toggle = false;
        }


        if (!menuItem?.active) {
          if (this.sidebarMenuService.activeParentMenu) {
            this.sidebarMenuService.activeParentMenu.active = false;
            this.sidebarMenuService.activeParentMenu.selected = false;
          }
          /** Auto-Expand */
          if (toggle) {
            menuItem.expanded = !menuItem.expanded;
          } else {
            menuItem.expanded = true;
          }
          menuItem.active = (menuItem.items.find(subImtem => subImtem.active === true) !== undefined);
          menuItem.selected = true;
          this.sidebarMenuService.activeParentMenu = menuItem;
        } else {
          /** Auto-Expand */
          if (toggle) {
            menuItem.expanded = !menuItem.expanded;
          } else {
            menuItem.expanded = true;
          }
          menuItem.selected = true;
        }
        break;
      case 'S':
      case 'SP':
        if (!menuItem?.active) {
          /* deactivate old parent */
          if (this.sidebarMenuService.activeParentMenu) {
            this.sidebarMenuService.activeParentMenu.active = false;
            this.sidebarMenuService.activeParentMenu.selected = false;
          }
          if (this.sidebarMenuService.activeChildMenu) {
            this.sidebarMenuService.activeChildMenu.active = false;
            this.sidebarMenuService.activeChildMenu.selected = false;
          }
          /* activate current  parent */
          this.sidebarMenuService.activeParentMenu = this.sidebarMenuService.findMenuItemById(this.sidebarMenuService.menuItems, menuItem.parentId);
          this.sidebarMenuService.activeParentMenu.active = true;
          this.sidebarMenuService.activeParentMenu.selected = true;
          menuItem.active = true;
          menuItem.selected = true;
          this.sidebarMenuService.activeChildMenu = menuItem;
        } else {
          menuItem.selected = true;
        }
        break;
    }

  }

  /**
   * Accessibility with cursor-keys and tab
   * @param event
   */
  onKeyDown(event: KeyboardEvent) {
    const currentIndex = this.links.findIndex(el => el === document.activeElement);
    if (event.key === 'ArrowDown') {
      let next: number;
      const currentMenuItem: SpsMenuItem = this.sidebarMenuService.menuItems.find(item => item.linkIndex === currentIndex);
      if (currentMenuItem?.type === 'P' && (!currentMenuItem.expanded || !this.layoutService.menuExpanded())) {
        next = currentMenuItem.nextCollapsedLinkIndex;
      } else {
        next = (currentIndex + 1) % this.links.length;
      }
      this.links[next].focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowUp') {
      let prev: number = (currentIndex - 1 + this.links.length) % this.links.length;
      const currentMenuItem: SpsMenuItem = this.sidebarMenuService.menuItems.find(item => item.linkIndex === currentIndex);
      if (currentMenuItem?.previousParentId) {
        const parentMenuItem: SpsMenuItem = this.sidebarMenuService.menuItems.find(item => item.id === currentMenuItem?.previousParentId);
        if (!parentMenuItem.expanded || !this.layoutService.menuExpanded() ) {
          prev = parentMenuItem.linkIndex;
        }
      }
      this.links[prev].focus();
      event.preventDefault();
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      (event.target as HTMLElement).click();
    }
  }
}
